import React, { useState, useEffect } from "react";
import SuiButton from '../../../../components/SoftButton';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Typography,
    Box,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    useTheme,
    useMediaQuery
} from "@mui/material";
import { IoLanguageOutline } from "react-icons/io5";
import { translateArticle } from "../../../../axios-client";
import { useTranslation } from 'react-i18next';
import PropTypes from "prop-types";
import SnackbarNotification from "../../../../reusableComponents/Snackbar/SnackbarNotification";
import {parseCustomPageRange} from "../../../../utlis/generalUtils";

const RegenerateTranslationPopup = ({ articleId, fileName, targetLanguage, currentPageNum, numPages, fetchData }) => {
    const { t, i18n } = useTranslation(['regenerateTranslation']);

    const [open, setOpen] = useState(false);
    const [option, setOption] = useState("current");
    const [pageCustom, setPageCustom] = useState("");
    const [errors, setErrors] = useState({ from: "", to: "" });
    const [isValid, setIsValid] = useState(true);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({
        show: false,
        snackbarMessage: "",
        severity: "",

    });
    const dir = i18n.dir();
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const toggleDialog = () => {
        setOpen((prev) => !prev);
        if (open) {
            setOption("current");
            setPageCustom("");
            setErrors({ custom: "" });
            setIsValid(true);
        }
    };

    useEffect(() => {
        if (option !== "range") {
            setErrors({ custom: "" });
            setIsValid(true);
            return;
        }

        const { isValid: valid, error: customError } = parseCustomPageRange(pageCustom, numPages, t);

        setErrors({ custom: customError });
        setIsValid(valid);
    }, [ pageCustom, option, numPages, t]);

    const handleConfirm = async () => {
        if (option === "range" && !isValid) return;
        setLoading(true);

        const payload = {
            article_id: articleId,
            filename: fileName,
            target_language: targetLanguage,
            page_nums: option === "range" ? pageCustom : option === "current" ? currentPageNum.toString() : '',
        };

        try {
            const res = await translateArticle(payload);
            setNotification({
                show: true,
                snackbarMessage: res.data.message,
                severity: "success",
            });
            fetchData();
        } catch (err) {
            console.error("Failed to trigger translation:", err);
        } finally {
            setLoading(false);
            toggleDialog();
        }
    };

    return (
        <>
            <Tooltip title={loading ? t("tooltip.translating") : t("tooltip.regenerateTranslation")}>
                <span>
                    <IconButton
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        sx={{
                            width: { xs: 28, sm: 32 },
                            height: { xs: 28, sm: 32 },
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            color: "#616161",
                            border: "1px solid #d8d6d6",
                            borderRadius:"5px",
                            p: 1,
                            "&:hover": {
                                borderColor: "#757575",
                                background: "#F5F5F5",
                            },
                        }}
                        onClick={toggleDialog} disabled={loading} style={{ opacity: 1 }}>
                        {loading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <IoLanguageOutline size={"20"}/>
                        )}
                    </IconButton>
                </span>
            </Tooltip>

            <Dialog open={open} onClose={toggleDialog} maxWidth="xs" fullWidth
                style={{direction: dir}}
            >
                <DialogTitle>
                    <Typography variant="h6" fontWeight="bold">
                        {t("translationTitle")}
                    </Typography>
                </DialogTitle>

                <Divider />

                <DialogContent>
                    <RadioGroup
                        value={option}
                        onChange={(e) => setOption(e.target.value)}
                        sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}
                    >
                        <FormControlLabel value="current" control={<Radio />} label={t("options.current")} />
                        <FormControlLabel value="all" control={<Radio />} label={t("options.all")} />
                        <FormControlLabel value="range" control={<Radio />} label={t("options.range")} />
                        {option === "range" && (
                            <Box display="flex" gap={2} mt={1} alignItems="flex-start">
                                <Box sx={{ display: "flex", flexDirection: "column"}}>
                                    <TextField
                                        id="page-custom"
                                        type="text"
                                        size="small"
                                        value={pageCustom}
                                        onChange={(e) => setPageCustom(e.target.value)}
                                        error={!!errors.custom}
                                        helperText={errors.custom}
                                        fullWidth
                                    />
                                    <small
                                        style={{fontSize: "10px"}}>
                                        {t("rangeHint")}
                                    </small>
                                </Box>
                            </Box>
                        )}
                    </RadioGroup>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <SuiButton variant="contained" color="light" onClick={toggleDialog} disabled={loading}
                               sx={{ml: 1, mr:  (dir === 'rtl' ? 1 : 0)}}
                    >
                        {t("buttons.cancel")}
                    </SuiButton>
                    <SuiButton
                        variant="contained"
                        color="primary"
                        onClick={handleConfirm}
                        disabled={(option === "range" && !isValid) || loading}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : t("buttons.confirm")}
                    </SuiButton>
                </DialogActions>
            </Dialog>
            <SnackbarNotification
                notification={notification}
                setNotification={setNotification}
            />
        </>
    );
};

RegenerateTranslationPopup.propTypes = {
    articleId: PropTypes.number,
    fileName: PropTypes.string,
    targetLanguage: PropTypes.string,
    currentPageNum: PropTypes.number,
    numPages: PropTypes.number,
    fetchData: PropTypes.func
};

export default RegenerateTranslationPopup;
